// Tenant resolver: pulls the custom claims that the
// custom_access_token auth hook (supabase/migrations/20260507151838_*)
// merges into every Supabase access token. Keep this pure so RLS-aware
// queries and tests can call it without a Supabase session.

const USER_TYPES = [
  'pharmacy_staff',
  'pharmacy_admin',
  'dispatcher',
  'driver',
  'platform_admin',
] as const;

export type UserType = (typeof USER_TYPES)[number];

export type TenantClaims = {
  tenantId: string | null;
  userType: UserType | null;
};

export const EMPTY_CLAIMS: TenantClaims = { tenantId: null, userType: null };

export function isUserType(value: unknown): value is UserType {
  return typeof value === 'string' && (USER_TYPES as readonly string[]).includes(value);
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const payload = parts[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json =
      typeof atob === 'function'
        ? atob(padded)
        : Buffer.from(padded, 'base64').toString('utf8');
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function extractTenantClaims(accessToken: string | null | undefined): TenantClaims {
  if (!accessToken) return EMPTY_CLAIMS;
  const payload = decodeJwtPayload(accessToken);
  if (!payload) return EMPTY_CLAIMS;

  const rawTenant = payload['tenant_id'];
  const tenantId = typeof rawTenant === 'string' && rawTenant.length > 0 ? rawTenant : null;

  const rawUserType = payload['user_type'];
  const userType = isUserType(rawUserType) ? rawUserType : null;

  // Per the auth hook (PR #7): platform_admin tokens carry NO tenant_id.
  // Everyone else MUST carry tenant_id. A non-admin token with no tenant_id
  // means the public.users row was missing or soft-deleted at mint time —
  // we treat it as unscoped (claims empty), which RLS will deny by default.
  if (userType !== 'platform_admin' && tenantId === null) {
    return { tenantId: null, userType };
  }

  return { tenantId, userType };
}
